'use client';

/**
 * Collapsed sidebar rail (64px) — Phase 2.
 *
 * Icon-only navigation. Every interactive surface carries a tooltip with
 * the full label so users can navigate by hover/focus alone. Keyboard
 * focus order:
 *   1. Brand / expand button
 *   2. Cmd-K trigger
 *   3. Pinned items (user-set order)
 *   4. Domain tile entry points (popover lists every visible item in the domain)
 *   5. Footer (Settings, Help)
 *
 * Active state mirrors the expanded sidebar — current path's domain or
 * pin gets the highlight + leading rail accent.
 */

import { RailCountBadge } from './rail-count-badge';
import { useMemo } from 'react';
import { useComplianceObligations } from '@/hooks/useComplianceObligations';
import { useComplianceSurfaceVisibility } from '@/lib/compliance/use-surface-visibility';
import {
  complianceSurfaceForHref,
  isFactDrivenComplianceSurfaceVisible,
} from '@/lib/compliance/hub-tile-visibility';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { isComplianceNavRouteApplicable } from '@/lib/navigation/compliance-applicability';
import Link from 'next/link';
import {
  PanelLeftOpen,
  Search,
  Settings,
  CircleHelp,
  Pin,
  MessageCircle,
  ChevronRight,
} from 'lucide-react';
import { Logo, Popover, Tooltip, CountBadge, controlSizeClassName } from '@statura/ui';
import {
  FOOTER_NAV_V2,
  flattenNavV2,
  type FlatNavV2Item,
  type NavBadgeCounts,
  type NavV2Group,
  type NavV2Item,
  type ProviderType,
} from '@/lib/navigation/nav-v2';
import { navV2SidebarLeafIsVisible, sidebarGroupsForBillingPlan } from './sidebar';
import { trackNavV2Event } from '@/lib/nav-v2/telemetry';
import { isComplianceOnlyPlan, type VisibleGoLiveScope } from '@/lib/billing';
import type { ProviderProgramContext } from '@/lib/navigation/provider-type';
import { ProviderChip } from './provider-chip';

const RAIL_WIDTH = 64;
const railControlClassName = `${controlSizeClassName('default')} w-11 shrink-0 !p-0`;
const railToolbarClassName = `${controlSizeClassName('toolbar')} w-9 shrink-0 !p-0`;

export interface NavV2SidebarCollapsedProps {
  currentPath: string;
  liveRole: string | null | undefined;
  providerType: ProviderType;
  programContext?: ProviderProgramContext;
  enabledModules: string[] | null;
  payrollRebuildOn?: boolean;
  billingPlan?: string | null;
  tenantId?: string | null;
  isDemoTenant?: boolean;
  visibleGoLiveScope?: VisibleGoLiveScope;
  tenantHiddenNavigationPaths?: readonly string[];
  frontlineSelfServiceOnly?: boolean;
  pinnedHrefs: readonly string[];
  badgeCounts?: NavBadgeCounts;
  onCmdK: () => void;
  onExpand: () => void;
  onHelpChat?: () => void;
}

export function NavV2SidebarCollapsed({
  currentPath,
  liveRole,
  providerType,
  programContext,
  enabledModules,
  payrollRebuildOn,
  billingPlan,
  tenantId,
  isDemoTenant,
  visibleGoLiveScope,
  tenantHiddenNavigationPaths,
  frontlineSelfServiceOnly,
  pinnedHrefs,
  badgeCounts,
  onCmdK,
  onExpand,
  onHelpChat,
}: NavV2SidebarCollapsedProps) {
  const governmentIntegrations = useGovernmentIntegrations();
  const { resolutionsById } = useComplianceObligations();
  const { data: surfaceVisibility } = useComplianceSurfaceVisibility();
  // Preserve full-suite rail presentation while matching the new cards' fact-driven gates.
  const complianceSurfaceVisibility = isComplianceOnlyPlan(billingPlan)
    ? surfaceVisibility
    : undefined;
  // Same entitlement + coordinator / facility-lead L1 allowlist as the expanded sidebar.
  const visibleGroups = useMemo<ReadonlyArray<NavV2Group & { items: readonly NavV2Item[] }>>(() => {
    return sidebarGroupsForBillingPlan(billingPlan, liveRole)
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) =>
            (complianceSurfaceForHref(item.href.split(/[?#]/, 1)[0] ?? item.href) &&
            isComplianceOnlyPlan(billingPlan)
              ? isFactDrivenComplianceSurfaceVisible(
                  item.href.split(/[?#]/, 1)[0] ?? item.href,
                  complianceSurfaceVisibility,
                )
              : isComplianceNavRouteApplicable(
                  item.href.split(/[?#]/, 1)[0] ?? item.href,
                  resolutionsById,
                )) &&
            navV2SidebarLeafIsVisible(item, {
              liveRole,
              billingPlan,
              providerType,
              providerProgramContext: programContext,
              enabledModules,
              payrollRebuildOn,
              tenantId,
              isDemoTenant,
              visibleGoLiveScope,
              tenantHiddenNavigationPaths,
              frontlineSelfServiceOnly,
              governmentIntegrations,
              complianceSurfaceVisibility,
            }),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [
    liveRole,
    providerType,
    programContext,
    enabledModules,
    payrollRebuildOn,
    billingPlan,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    governmentIntegrations,
    resolutionsById,
    complianceSurfaceVisibility,
  ]);

  const visibleHrefsSet = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    for (const g of visibleGroups) for (const it of g.items) s.add(it.href);
    return s;
  }, [visibleGroups]);

  const itemLookup = useMemo<Map<string, FlatNavV2Item>>(() => {
    const m = new Map<string, FlatNavV2Item>();
    for (const it of flattenNavV2(visibleGroups)) m.set(it.href, it);
    return m;
  }, [visibleGroups]);

  const visiblePins = useMemo<FlatNavV2Item[]>(() => {
    return pinnedHrefs
      .map((h) => itemLookup.get(h))
      .filter((it): it is FlatNavV2Item => !!it && visibleHrefsSet.has(it.href));
  }, [pinnedHrefs, itemLookup, visibleHrefsSet]);

  // Phase 2 retro audit P3 (codex pass 2): longest-prefix winner for pins —
  // visiting /analytics/board-pack when both /analytics and
  // /analytics/board-pack are pinned should mark only the most specific
  // one as the current page. Matches `PinnedList` behaviour.
  const activePinHref = useMemo<string | null>(() => {
    let best: { href: string; length: number } | null = null;
    for (const item of visiblePins) {
      const matches = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
      if (!matches) continue;
      if (!best || item.href.length > best.length) {
        best = { href: item.href, length: item.href.length };
      }
    }
    return best?.href ?? null;
  }, [visiblePins, currentPath]);

  const visibleFooter = useMemo(
    () =>
      FOOTER_NAV_V2.filter((item) =>
        navV2SidebarLeafIsVisible(item, {
          liveRole,
          providerType,
          providerProgramContext: programContext,
          enabledModules,
          payrollRebuildOn,
          tenantId,
          isDemoTenant,
          visibleGoLiveScope,
          tenantHiddenNavigationPaths,
          frontlineSelfServiceOnly,
          governmentIntegrations,
        }),
      ),
    [
      liveRole,
      providerType,
      programContext,
      enabledModules,
      payrollRebuildOn,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      frontlineSelfServiceOnly,
      governmentIntegrations,
    ],
  );

  return (
    <aside
      aria-label="Workspace navigation (collapsed)"
      style={{ width: RAIL_WIDTH, flex: `0 0 ${RAIL_WIDTH}px` }}
      className="ds-shell-navigation flex h-full min-h-0 flex-col items-center gap-1 border-r border-control-border/30 bg-surface-canvas py-3"
      data-nav-v2-sidebar="collapsed"
    >
      {/* Brand → expand */}
      <Tooltip content="Expand sidebar  ]" side="right">
        <button
          type="button"
          onClick={onExpand}
          aria-label="Expand sidebar"
          className={`${railToolbarClassName} inline-flex items-center justify-center rounded-control border border-control-border/30 bg-surface-sheet hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
        >
          <Logo variant="icon" width={24} height={24} />
          <span className="sr-only">Statura Care home</span>
          <PanelLeftOpen className="sr-only" aria-hidden="true" />
        </button>
      </Tooltip>

      <ProviderChip
        providerType={providerType}
        programContext={programContext}
        tenantId={tenantId}
        compact
      />

      <div className="my-1 h-px w-8 shrink-0 bg-control-border/30" />

      {/* Cmd-K */}
      <Tooltip content="Search · ⌘K" side="right">
        <button
          type="button"
          onClick={onCmdK}
          aria-label="Open command palette"
          className={`${railToolbarClassName} inline-flex items-center justify-center rounded-control border border-control-border/30 bg-surface-sheet hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
        >
          <Search className="h-4 w-4 text-text-muted" aria-hidden="true" />
        </button>
      </Tooltip>

      <div className="my-1 h-px w-8 shrink-0 bg-control-border/30" />

      {/* PIN section */}
      <RailSectionLabel>Pinned navigation</RailSectionLabel>
      <div className="flex max-h-[20vh] min-h-0 shrink-0 w-full flex-col items-center gap-0.5 overflow-y-auto overflow-x-hidden">
        {visiblePins.length === 0 ? (
          <Tooltip content="No pins yet — expand to add" side="right">
            <button
              type="button"
              onClick={onExpand}
              aria-label="No pins yet — expand to add"
              className={`${railToolbarClassName} inline-flex items-center justify-center rounded-control text-text-muted hover:bg-surface-sheet focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
            >
              <Pin className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        ) : (
          visiblePins.map((item) => (
            <RailLink
              key={item.href}
              item={item}
              // Phase 2 retro audit P3 (codex pass 2): single-winner pin
              // by longest-prefix — see `activePinHref` above.
              active={item.href === activePinHref}
              badgeCount={item.badgeKey ? badgeCounts?.[item.badgeKey] : undefined}
            />
          ))
        )}
      </div>

      <div className="my-1 h-px w-8 shrink-0 bg-control-border/30" />

      {/* DOMAIN section */}
      <RailSectionLabel>Navigation groups</RailSectionLabel>
      <div className="flex w-full min-h-0 flex-1 flex-col items-center gap-0.5 overflow-x-hidden overflow-y-auto overscroll-contain py-1">
        {visibleGroups.map((g) => {
          const first = g.items[0];
          if (!first) return null;
          // Phase 2 retro audit P3: prefix-match so /clients/abc keeps
          // the Care domain rail icon highlighted.
          const isActive =
            !activePinHref &&
            g.items.some((i) => currentPath === i.href || currentPath.startsWith(`${i.href}/`));
          if (g.items.length > 1) {
            return (
              <RailDomainMenu
                key={g.id}
                group={g}
                currentPath={currentPath}
                active={isActive}
                badgeCounts={badgeCounts}
              />
            );
          }
          return (
            <RailLink
              key={g.id}
              item={{
                ...first,
                label: g.title,
                href: first.href,
                icon: g.icon,
                groupId: g.id,
                groupTitle: g.title,
                groupIcon: g.icon,
              }}
              active={isActive}
              badgeCount={first.badgeKey ? badgeCounts?.[first.badgeKey] : undefined}
            />
          );
        })}
      </div>

      <div className="my-1 h-px w-8 shrink-0 bg-control-border/30" />

      {/* Footer — pinned to viewport bottom via sticky sidebar shell */}
      <div className="mt-auto flex shrink-0 flex-col items-center gap-0.5">
        {visibleFooter.map((item) => {
          const Icon = item.label === 'Settings' ? Settings : CircleHelp;
          const isActive = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
          return (
            <Tooltip key={item.href} content={item.label} side="right">
              <Link
                href={item.href}
                onClick={() =>
                  trackNavV2Event('nav_v2_item_clicked', {
                    source: 'sidebar-footer',
                    route: item.href,
                  })
                }
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
                className={[
                  `${railToolbarClassName} inline-flex items-center justify-center rounded-control focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`,
                  isActive
                    ? 'border border-control-border/30 bg-selected text-selected-foreground'
                    : 'text-text-muted hover:bg-surface-sheet/70',
                ].join(' ')}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Tooltip>
          );
        })}
        {onHelpChat && (
          <Tooltip content="Help chat" side="right">
            <button
              type="button"
              onClick={() => {
                trackNavV2Event('nav_v2_item_clicked', {
                  source: 'sidebar-footer',
                  route: 'help-chat',
                });
                onHelpChat();
              }}
              aria-label="Open help chat"
              className={`${railToolbarClassName} inline-flex items-center justify-center rounded-control text-text-muted hover:bg-surface-sheet/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </div>
    </aside>
  );
}

function RailSectionLabel({ children }: { children: React.ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

function RailLink({
  item,
  active,
  badgeCount,
}: {
  item: FlatNavV2Item;
  active: boolean;
  badgeCount?: number;
}) {
  const Icon = item.icon;
  return (
    <Tooltip content={item.label} side="right">
      <Link
        href={item.href}
        onClick={() =>
          trackNavV2Event('nav_v2_item_clicked', {
            source: 'sidebar-collapsed',
            route: item.href,
          })
        }
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        className={[
          `${railControlClassName} relative inline-flex items-start pt-1 justify-center rounded-control border border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`,
          active ? 'border-control-border/30 bg-selected' : 'hover:bg-surface-sheet/70',
        ].join(' ')}
      >
        <Icon
          className={['h-4 w-4', active ? 'text-selected-foreground' : 'text-text-muted'].join(' ')}
          aria-hidden="true"
        />
        {active && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-action"
          />
        )}
        <RailCountBadge count={badgeCount} />
      </Link>
    </Tooltip>
  );
}

function RailDomainMenu({
  group,
  currentPath,
  active,
  badgeCounts,
}: {
  group: NavV2Group & { items: readonly NavV2Item[] };
  currentPath: string;
  active: boolean;
  badgeCounts?: NavBadgeCounts;
}) {
  const Icon = group.icon;
  const groupBadgeCount = group.items.reduce(
    (sum, item) => sum + (item.badgeKey ? (badgeCounts?.[item.badgeKey] ?? 0) : 0),
    0,
  );
  const trigger = (
    <button
      type="button"
      aria-label={`${group.title} navigation`}
      title={`${group.title} navigation`}
      className={[
        `${railControlClassName} relative inline-flex items-start pt-1 justify-center rounded-control border border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-focus`,
        active ? 'border-control-border/30 bg-selected' : 'hover:bg-surface-sheet/70',
      ].join(' ')}
      data-nav-v2-collapsed-domain-trigger={group.id}
    >
      <Icon
        className={['h-4 w-4', active ? 'text-selected-foreground' : 'text-text-muted'].join(' ')}
        aria-hidden="true"
      />
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-action"
        />
      )}
      <RailCountBadge count={groupBadgeCount} />
    </button>
  );

  return (
    <Popover
      trigger={trigger}
      side="right"
      align="start"
      role="menu"
      ariaLabel={`${group.title} navigation`}
      zIndex={75}
      className="ds-shell-navigation rounded-overlay shadow-popover w-64 max-h-96 p-1"
    >
      <div className="border-b border-control-border/30 px-2.5 py-1.5">
        <p className="text-caption font-semibold uppercase tracking-wider text-selected-foreground">
          {group.title}
        </p>
        {group.description && (
          <p className="mt-0.5 text-caption text-text-muted">{group.description}</p>
        )}
      </div>
      <ul role="menu" className="flex flex-col gap-px py-1">
        {group.items.map((item) => {
          const ItemIcon = item.icon;
          const itemActive = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
          const badgeCount = item.badgeKey ? (badgeCounts?.[item.badgeKey] ?? 0) : 0;
          return (
            <li key={item.href} role="none">
              <Link
                role="menuitem"
                href={item.href}
                onClick={() =>
                  trackNavV2Event('nav_v2_item_clicked', {
                    source: 'sidebar-collapsed',
                    route: item.href,
                    variant: 'rail-popover',
                  })
                }
                aria-current={itemActive ? 'page' : undefined}
                className={[
                  'flex items-center gap-2 rounded-control px-2 py-1.5 text-caption',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                  itemActive
                    ? 'bg-selected font-semibold text-selected-foreground'
                    : 'text-text-heading hover:bg-surface-canvas',
                ].join(' ')}
              >
                <ItemIcon className="h-3 w-3 shrink-0 text-text-muted" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {badgeCount > 0 ? (
                  <CountBadge
                    count={badgeCount}
                    label="items need attention"
                    className="self-center bg-status-warning-bg text-status-warning-fg"
                  />
                ) : null}
                <ChevronRight className="h-3 w-3 shrink-0 text-text-muted" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
    </Popover>
  );
}
